import { Controller } from "@hotwired/stimulus";

/**
 * Manages a survey's scoring configuration form: the enable toggle, score
 * direction, flag threshold, MCID, and a dynamic list of interpretation
 * bands.
 *
 * The entire configuration is serialized to a single hidden JSON field
 * (`scoring_config`) that maps to a JSONB column on the Survey model. The
 * `scoring_mode` field is toggled between "sum" and "" to enable/disable
 * scoring.
 *
 * Band model (simplified F4 design): each band row carries only a single
 * `starts_at` threshold. The band's `min` is its threshold; its `max` is
 * derived as `next.starts_at - 1` (the last band runs to `maxScore`). The
 * first band is locked to the scale minimum (0). This makes gaps and
 * overlaps structurally impossible — `serializeRanges` still emits explicit
 * `{min, max}` pairs so the backend (`Survey#interpretation_for_score`,
 * scoring service, model validation) is unchanged.
 *
 * Existing configs with non-contiguous `{min, max}` ranges are snapped
 * contiguous on load: each stored `min` becomes a `starts_at`, sorted
 * ascending, and the bounds are re-derived.
 */
// The bands table caps at 10 rows — the color palette has 10 entries, and
// more interpretation bands than that stops being a usable scale.
const MAX_BANDS = 10;

export default class extends Controller {
  static targets = [
    "enableToggle",
    "modeField",
    "hiddenField",
    "configContainer",
    "directionSelect",
    "flagThreshold",
    "flagThresholdLink",
    "flagThresholdField",
    "mcid",
    "mcidLink",
    "rangesContainer",
    "rangeTemplate",
    "addButton",
  ];

  static values = {
    // Maximum possible score — used to derive the last band's upper bound.
    maxScore: { type: Number, default: 0 },
    // Actions already used across any of this survey's interpretation ranges.
    // Rendered as suggestion options in each range's tom-select.
    knownActions: { type: Array, default: [] },
  };

  declare readonly enableToggleTarget: HTMLInputElement;
  declare readonly modeFieldTarget: HTMLInputElement;
  declare readonly hiddenFieldTarget: HTMLInputElement;
  declare readonly configContainerTargets: HTMLElement[];
  declare readonly directionSelectTarget: HTMLSelectElement;
  declare readonly hasDirectionSelectTarget: boolean;
  declare readonly flagThresholdTarget: HTMLInputElement;
  declare readonly hasFlagThresholdTarget: boolean;
  declare readonly flagThresholdLinkTarget: HTMLElement;
  declare readonly hasFlagThresholdLinkTarget: boolean;
  declare readonly flagThresholdFieldTarget: HTMLElement;
  declare readonly hasFlagThresholdFieldTarget: boolean;
  declare readonly mcidTarget: HTMLInputElement;
  declare readonly hasMcidTarget: boolean;
  declare readonly mcidLinkTarget: HTMLElement;
  declare readonly hasMcidLinkTarget: boolean;
  declare readonly rangesContainerTarget: HTMLElement;
  declare readonly rangeTemplateTarget: HTMLTemplateElement;
  declare readonly addButtonTarget: HTMLButtonElement;
  declare readonly hasAddButtonTarget: boolean;
  declare readonly maxScoreValue: number;
  declare readonly knownActionsValue: string[];

  private knownActions: Set<string> = new Set();

  connect() {
    this.knownActions = new Set(this.knownActionsValue);
    this.loadExistingConfig();
    this.applyVisibility();
    this.syncOptionalFields();
    // Normalize the hidden field to the current serialization immediately, so
    // a save with no further edits still persists derived min/max bounds and
    // the snapped-contiguous result from loadExistingConfig.
    this.updateConfig();
    this.updateAddButtonState();
  }

  // Reveal the auto-flag input (shown as a "Set value" link until used).
  revealFlagThreshold(event?: Event) {
    event?.preventDefault();
    if (this.hasFlagThresholdLinkTarget) this.flagThresholdLinkTarget.classList.add("hidden");
    if (this.hasFlagThresholdFieldTarget) this.flagThresholdFieldTarget.classList.remove("hidden");
    if (this.hasFlagThresholdTarget) this.flagThresholdTarget.focus();
  }

  // Reveal the MCID input (shown as a "Set value" link until used).
  revealMcid(event?: Event) {
    event?.preventDefault();
    if (this.hasMcidLinkTarget) this.mcidLinkTarget.classList.add("hidden");
    if (this.hasMcidTarget) {
      this.mcidTarget.classList.remove("hidden");
      this.mcidTarget.focus();
    }
  }

  // On load, swap any optional setting that already has a value from its
  // "Set value" link to its input.
  private syncOptionalFields() {
    if (this.hasFlagThresholdTarget && this.flagThresholdTarget.value.trim() !== "") {
      if (this.hasFlagThresholdLinkTarget) this.flagThresholdLinkTarget.classList.add("hidden");
      if (this.hasFlagThresholdFieldTarget) this.flagThresholdFieldTarget.classList.remove("hidden");
    }
    if (this.hasMcidTarget && this.mcidTarget.value.trim() !== "") {
      if (this.hasMcidLinkTarget) this.mcidLinkTarget.classList.add("hidden");
      this.mcidTarget.classList.remove("hidden");
    }
  }

  // Show/hide the config section and update the hidden scoring_mode field
  // based on the enable toggle.
  toggle() {
    this.modeFieldTarget.value = this.enableToggleTarget.checked ? "sum" : "";
    this.applyVisibility();
  }

  // Append a new empty band row from the template, seed its threshold just
  // past the previous band, and focus it.
  addRange(event?: Event) {
    event?.preventDefault();
    // Guards the keyboard/programmatic path; the button is also disabled at
    // the cap by updateAddButtonState.
    if (this.bandCount() >= MAX_BANDS) return;

    const content = this.rangeTemplateTarget.innerHTML;
    this.rangesContainerTarget.insertAdjacentHTML("beforeend", content);

    const newRow = this.rangesContainerTarget.lastElementChild as HTMLElement | null;
    this.injectKnownActionOptions(newRow);
    this.lockFirstRow();
    this.autofillThreshold(newRow);

    const startsAtInput = newRow?.querySelector<HTMLInputElement>("input[data-field='starts_at']");
    startsAtInput?.focus();

    this.updateConfig();
    this.updateAddButtonState();
    this.notifyConfigChanged();
  }

  // Remove a band row. The clicked button's closest [data-range-row] is removed.
  removeRange(event: Event) {
    event.preventDefault();
    const button = event.currentTarget as HTMLElement;
    const row = button.closest<HTMLElement>("[data-range-row]");
    row?.remove();
    this.lockFirstRow();
    this.updateConfig();
    this.updateAddButtonState();
    this.notifyConfigChanged();
  }

  // Number of band rows currently in the table.
  private bandCount(): number {
    return this.rangesContainerTarget.querySelectorAll("[data-range-row]").length;
  }

  // Tell form-auto-save a structural change happened. Band-field edits bubble
  // a native `input`, but add/remove are clicks — and the remove button
  // detaches itself before a sibling `form-auto-save#schedule` action can
  // fire. Dispatching a bubbling `change` from the always-attached hidden
  // field is what reliably reaches the auto-save controller.
  private notifyConfigChanged() {
    this.hiddenFieldTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Disable the "Add band" button once the table is at the MAX_BANDS cap.
  private updateAddButtonState() {
    if (!this.hasAddButtonTarget) return;
    this.addButtonTarget.disabled = this.bandCount() >= MAX_BANDS;
  }

  // Rebuild the JSON config from current form state and write to the hidden
  // field. Called on any input change inside the scoring section.
  updateConfig() {
    this.clampThresholds();

    const config: Record<string, unknown> = {};

    const direction = this.hasDirectionSelectTarget ? this.directionSelectTarget.value : "";
    if (direction) config.score_direction = direction;

    if (this.hasFlagThresholdTarget && this.flagThresholdTarget.value !== "") {
      const parsed = parseFloat(this.flagThresholdTarget.value);
      if (!Number.isNaN(parsed)) config.flag_threshold = parsed;
    }

    if (this.hasMcidTarget && this.mcidTarget.value !== "") {
      const parsed = parseFloat(this.mcidTarget.value);
      if (!Number.isNaN(parsed)) config.mcid = parsed;
    }

    const ranges = this.serializeRanges();
    if (ranges.length > 0) config.interpretation_ranges = ranges;

    this.hiddenFieldTarget.value = JSON.stringify(config);

    // Learn new actions the user has added in any range
    for (const range of ranges) {
      if (Array.isArray(range.actions)) {
        for (const action of range.actions as string[]) {
          this.knownActions.add(action);
        }
      }
    }
  }

  // Build the interpretation_ranges array. Each band's `min` is its
  // `starts_at` threshold (the first row is forced to 0); its `max` is the
  // next band's threshold minus one, or `maxScore` for the last band.
  private serializeRanges(): Array<Record<string, unknown>> {
    const rows = Array.from(
      this.rangesContainerTarget.querySelectorAll<HTMLElement>("[data-range-row]"),
    );

    const thresholds = rows.map((row, index) => {
      if (index === 0) return 0;
      const raw = this.fieldValue(row, "starts_at");
      const parsed = parseInt(raw, 10);
      return Number.isNaN(parsed) ? NaN : parsed;
    });

    return rows.map((row, index) => {
      const range: Record<string, unknown> = {};

      const startsAt = thresholds[index];
      if (!Number.isNaN(startsAt)) range.min = startsAt;

      if (index < rows.length - 1) {
        const next = thresholds[index + 1];
        if (!Number.isNaN(next)) range.max = next - 1;
      } else if (Number.isFinite(this.maxScoreValue) && this.maxScoreValue > 0) {
        range.max = this.maxScoreValue;
      }

      const scalarFields = ["label", "description", "review_type", "recommendations"];
      for (const field of scalarFields) {
        const value = this.fieldValue(row, field);
        if (value !== "") range[field] = value;
      }

      const actionsSelect = row.querySelector<HTMLSelectElement>("select[data-field='actions']");
      if (actionsSelect) {
        const actions = Array.from(actionsSelect.selectedOptions)
          .map((o) => o.value.trim())
          .filter((v) => v !== "");
        if (actions.length > 0) range.actions = actions;
      }

      return range;
    });
  }

  private fieldValue(row: HTMLElement, field: string): string {
    const input = row.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      `[data-field='${field}']`,
    );
    return input?.value.trim() ?? "";
  }

  private loadExistingConfig() {
    const raw = this.hiddenFieldTarget.value;
    if (!raw || raw.trim() === "") return;

    try {
      const config = JSON.parse(raw);

      if (this.hasDirectionSelectTarget && config.score_direction) {
        this.directionSelectTarget.value = config.score_direction;
      }

      if (this.hasFlagThresholdTarget && config.flag_threshold != null) {
        this.flagThresholdTarget.value = String(config.flag_threshold);
      }

      if (this.hasMcidTarget && config.mcid != null) {
        this.mcidTarget.value = String(config.mcid);
      }

      // Snap contiguous: sort stored ranges by `min` and render each one's
      // `min` as its `starts_at`. Any stored gap or overlap collapses — the
      // simplified model derives upper bounds from the next threshold.
      const ranges: Array<Record<string, unknown>> = Array.isArray(config.interpretation_ranges)
        ? [...config.interpretation_ranges]
        : [];
      ranges.sort((a, b) => Number(a.min ?? 0) - Number(b.min ?? 0));
      for (const range of ranges) {
        this.renderRange(range);
      }
      this.lockFirstRow();
    } catch (error) {
      console.error("Failed to parse existing scoring_config:", error);
    }
  }

  private renderRange(range: Record<string, unknown>) {
    this.rangesContainerTarget.insertAdjacentHTML("beforeend", this.rangeTemplateTarget.innerHTML);
    const row = this.rangesContainerTarget.lastElementChild as HTMLElement | null;
    if (!row) return;

    const setField = (field: string, value: unknown) => {
      if (value == null) return;
      const input = row.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
        `[data-field='${field}']`,
      );
      if (input) input.value = String(value);
    };

    // The stored `min` becomes the band's editable threshold.
    setField("starts_at", range.min);
    setField("label", range.label);
    setField("description", range.description);
    setField("review_type", range.review_type);
    setField("recommendations", range.recommendations);

    const selectedActions = new Set<string>(
      Array.isArray(range.actions) ? (range.actions as string[]) : [],
    );
    this.injectKnownActionOptions(row, selectedActions);
  }

  // The first band always starts at the scale minimum (0). Its threshold
  // input is read-only and its delete button disabled so the band can't be
  // removed or re-based — every other band's lower bound is relative to it.
  private lockFirstRow() {
    const rows = this.rangesContainerTarget.querySelectorAll<HTMLElement>("[data-range-row]");
    rows.forEach((row, index) => {
      const startsAt = row.querySelector<HTMLInputElement>("input[data-field='starts_at']");
      const removeButton = row.querySelector<HTMLButtonElement>(
        "[data-action~='scoring-config#removeRange']",
      );
      const locked = index === 0;

      if (startsAt) {
        startsAt.readOnly = locked;
        startsAt.classList.toggle("bg-slate-100", locked);
        startsAt.classList.toggle("text-slate-400", locked);
        if (locked) startsAt.value = "0";
      }
      if (removeButton) {
        removeButton.disabled = locked;
        removeButton.classList.toggle("invisible", locked);
      }
    });
  }

  // Seed a freshly-added band's threshold one past the previous band, so the
  // list stays strictly increasing without the author having to guess.
  // The first band is locked to 0 by lockFirstRow, so this only runs for
  // bands at index >= 1.
  private autofillThreshold(newRow: HTMLElement | null) {
    if (!newRow) return;
    const rows = Array.from(
      this.rangesContainerTarget.querySelectorAll<HTMLElement>("[data-range-row]"),
    );
    const index = rows.indexOf(newRow);
    if (index <= 0) return;

    const startsAt = newRow.querySelector<HTMLInputElement>("input[data-field='starts_at']");
    if (!startsAt || startsAt.value !== "") return;

    const prevRaw =
      rows[index - 1].querySelector<HTMLInputElement>("input[data-field='starts_at']")?.value ?? "";
    const prev = parseInt(prevRaw, 10);
    const base = Number.isNaN(prev) ? index : prev + 1;
    startsAt.value = String(this.clampToScale(base));
  }

  // Keep every band's threshold within [0, maxScore]. The first band is
  // locked to 0, so it is skipped.
  private clampThresholds() {
    if (!Number.isFinite(this.maxScoreValue) || this.maxScoreValue <= 0) return;
    const rows = this.rangesContainerTarget.querySelectorAll<HTMLElement>("[data-range-row]");
    rows.forEach((row, index) => {
      if (index === 0) return;
      const input = row.querySelector<HTMLInputElement>("input[data-field='starts_at']");
      if (!input || input.value === "") return;
      const parsed = parseInt(input.value, 10);
      if (Number.isNaN(parsed)) return;
      const clamped = this.clampToScale(parsed);
      if (clamped !== parsed) input.value = String(clamped);
    });
  }

  private clampToScale(value: number): number {
    const upper =
      Number.isFinite(this.maxScoreValue) && this.maxScoreValue > 0
        ? this.maxScoreValue
        : value;
    return Math.min(Math.max(value, 0), upper);
  }

  // Injects <option> elements into a row's actions multi-select, synchronously
  // after the row HTML is inserted but BEFORE tom-select's async connect fires.
  private injectKnownActionOptions(row: HTMLElement | null, selectedActions: Set<string> = new Set()) {
    if (!row) return;
    const actionsSelect = row.querySelector<HTMLSelectElement>("select[data-field='actions']");
    if (!actionsSelect) return;

    const seen = new Set<string>();

    for (const action of this.knownActions) {
      seen.add(action);
      const option = document.createElement("option");
      option.value = action;
      option.text = action;
      option.selected = selectedActions.has(action);
      actionsSelect.appendChild(option);
    }

    for (const action of selectedActions) {
      if (seen.has(action)) continue;
      const option = document.createElement("option");
      option.value = action;
      option.text = action;
      option.selected = true;
      actionsSelect.appendChild(option);
      this.knownActions.add(action);
    }
  }

  private applyVisibility() {
    const enabled = this.enableToggleTarget.checked;
    for (const container of this.configContainerTargets) {
      container.classList.toggle("hidden", !enabled);
    }
  }
}
